/**
 * MultiView — мультивыбор с частичным зачётом (kind="multi", Фаза 14, эпик C;
 * макеты §2.5 спеки phase-14-interface-spec).
 *
 * `variant="answer"` (игрок): чекбокс-переключение вариантов (множество, не
 * один клик) + счётчик «Выбрано: N» + ЯВНАЯ кнопка «Ответить» (disabled при 0
 * отмеченных — ответ ≠ один клик, «другой UX» плана §1); после submit — лока
 * опций и «✓ Ответ отправлен» (прецедент word). Reveal: три состояния опции —
 * верный/лишний/пропущенный + строка «Зачтено k из m».
 *
 * `variant="spectate"` (ведущий): карточки вариантов с барами counts (счётчик —
 * абсолютное число отметок; сумма может превышать «N из M» = answered — игрок
 * отмечает несколько, §2.4); reveal подсвечивает все верные варианты.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { multiCorrectOptions, multiOptions, type PlayerAnswer } from '../../protocol';
import { FormulaText } from '../FormulaText';
import { optionLabel } from '../ui-helpers';
import type { QuestionBodyProps } from './types';

/** Множественный выбор игрока (Set для O(1) переключения). */
function toSet(selected: number[]): Set<number> {
  return new Set(selected);
}

export function MultiView({
  question,
  variant,
  myAnswer,
  reveal,
  counts,
  onAnswer,
}: QuestionBodyProps): JSX.Element {
  const { t } = useTranslation();
  const options = multiOptions(question);
  const initial = myAnswer?.kind === 'multi' ? myAnswer.data.selected : [];
  const [selected, setSelected] = useState<number[]>(initial);
  const [submitted, setSubmitted] = useState(myAnswer?.kind === 'multi');

  const locked = variant === 'answer' && (submitted || reveal !== null);
  const selectedSet = toSet(selected);
  const correctSet = toSet(multiCorrectOptions(reveal));
  const revealed = reveal !== null;

  function toggle(i: number): void {
    if (locked) return;
    setSelected((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  }

  function handleSubmit(): void {
    if (locked || selected.length === 0) return;
    setSubmitted(true);
    const answer: PlayerAnswer = { kind: 'multi', data: { selected: [...selected] } };
    onAnswer(answer);
  }

  // ── Spectate (ведущий): read-only карточки + counts-бары; reveal подсвечивает
  // все верные варианты (метка «✓ верный» на каждой).
  if (variant === 'spectate') {
    const totalMarks = counts ? counts.reduce((a, b) => a + b, 0) : 0;
    return (
      <div className="multi-view">
        <div className="options">
          {options.map((option, i) => {
            const count = counts?.[i] ?? 0;
            const pct = totalMarks > 0 ? Math.round((count / totalMarks) * 100) : 0;
            const isCorrect = revealed && correctSet.has(i);
            const classes = ['option', 'option--spectate'];
            if (isCorrect) classes.push('option--correct');
            return (
              <div key={i} className={classes.join(' ')} role="listitem">
                <span className="option-index">{optionLabel(i)}</span>
                <FormulaText className="option-text" text={option} />
                {counts ? (
                  <span className="option-stat">
                    <span className="stat-bar" aria-hidden="true">
                      <span className="stat-bar-fill" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="option-count">{count}</span>
                  </span>
                ) : null}
                {isCorrect ? <span className="option-mark">{t('options.correctMark')}</span> : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Answer (игрок).
  const hit = selected.filter((i) => correctSet.has(i)).length;

  function optionClass(i: number): string {
    const chosen = selectedSet.has(i);
    const classes = ['option'];
    if (revealed) {
      // Reveal: верный — зелёный (независимо от выбора игрока), отмеченный
      // неверный — красный («лишний»), прочие — нейтральные.
      if (correctSet.has(i)) classes.push('option--correct');
      else if (chosen) classes.push('option--wrong');
    } else if (chosen) {
      classes.push('option--chosen');
    }
    if (locked) classes.push('option--disabled');
    return classes.join(' ');
  }

  return (
    <div className="multi-view">
      <div className="options">
        {options.map((option, i) => {
          const chosen = selectedSet.has(i);
          const isCorrect = revealed && correctSet.has(i);
          // Три состояния reveal (§2.5): верный (в т.ч. пропущенный) / лишний.
          const missed = revealed && isCorrect && !chosen;
          const extra = revealed && !isCorrect && chosen;
          return (
            <button
              key={i}
              type="button"
              className={optionClass(i)}
              disabled={locked}
              onClick={() => toggle(i)}
              aria-pressed={chosen}
              data-testid={`answer-multi-option-${i}`}
              aria-label={t('options.optionAria', { label: optionLabel(i), text: option })}
            >
              <span className="multi-check" aria-hidden="true">
                {chosen ? '☑' : '☐'}
              </span>
              <span className="option-index">{optionLabel(i)}</span>
              <FormulaText className="option-text" text={option} />
              {isCorrect ? <span className="option-mark">{t('options.correctMark')}</span> : null}
              {missed ? <span className="option-mark">{t('multi.missedMark')}</span> : null}
              {extra ? <span className="option-mark">{t('multi.extraMark')}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="open-view__row">
        <span className="q-hint small" data-testid="answer-multi-selected-count">
          {t('join.answer.selectedCount', { n: selected.length })}
        </span>
        {submitted && !revealed ? (
          <span className="q-hint q-hint--ok" data-testid="multi-answered">
            {t('open.answered')}
          </span>
        ) : null}
        <button
          type="button"
          className="cta open-view__submit"
          disabled={locked || selected.length === 0}
          onClick={handleSubmit}
          data-testid="answer-multi-submit"
        >
          {t('join.answer.submit')}
        </button>
      </div>

      {revealed ? (
        <p className="q-hint" data-testid="answer-multi-partial">
          {t('multi.partial', { hit, total: correctSet.size })}
        </p>
      ) : null}
    </div>
  );
}
